import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Shield,
  Gamepad2,
  Users as UsersIcon,
  BarChart3,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Ban,
  RotateCcw,
} from 'lucide-react';
import { useAuthStore } from '../stores/useAuthStore';
import { useOnlinePlayers } from '../hooks/useOnlinePlayers';
import { useAppConfig, setGameDisabled } from '../hooks/useAppConfig';
import { GAMES } from '../games/registry';
import {
  firestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  updateDoc,
  getDoc,
} from '../lib/firebase';
import type { Profile } from '../lib/firebase';
import { toast } from 'sonner';

type Tab = 'overview' | 'games' | 'users';

export default function AdminPanel() {
  const user = useAuthStore((s) => s.user);
  const authLoading = useAuthStore((s) => s.loading);
  const [tab, setTab] = useState<Tab>('overview');

  if (authLoading) {
    return (
      <div className="py-16 flex justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  if (!user || !user.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="py-8">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-8 h-8 text-pink-400" />
        <h1 className="text-3xl font-bold text-white">Admin Panel</h1>
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10">
        <TabButton active={tab === 'overview'} onClick={() => setTab('overview')} icon={<BarChart3 className="w-4 h-4" />} label="Overview" />
        <TabButton active={tab === 'games'} onClick={() => setTab('games')} icon={<Gamepad2 className="w-4 h-4" />} label="Games" />
        <TabButton active={tab === 'users'} onClick={() => setTab('users')} icon={<UsersIcon className="w-4 h-4" />} label="Users" />
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'games' && <GamesTab />}
      {tab === 'users' && <UsersTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition ${
        active
          ? 'border-pink-500 text-white'
          : 'border-transparent text-gray-400 hover:text-white'
      }`}
    >
      {icon}
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
}

function OverviewTab() {
  const { onlinePlayers, isConnected } = useOnlinePlayers();
  const { disabledGames } = useAppConfig();
  const [stats, setStats] = useState<{ profiles: number; banned: number; recentGames: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profilesSnap, bannedSnap, gamesSnap] = await Promise.all([
          getDocs(query(collection(firestore, 'profiles'), limit(1000))),
          getDocs(query(collection(firestore, 'profiles'), where('is_banned', '==', true), limit(1000))),
          getDocs(query(collection(firestore, 'gameResults'), orderBy('created_at', 'desc'), limit(100))),
        ]);
        if (cancelled) return;
        setStats({
          profiles: profilesSnap.size,
          banned: bannedSnap.size,
          recentGames: gamesSnap.size,
        });
      } catch (err) {
        console.error('Failed to load admin stats', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard
        label="Online Now"
        value={isConnected ? String(onlinePlayers) : '—'}
        accent="text-green-400"
      />
      <StatCard
        label="Total Profiles"
        value={loading ? '…' : String(stats?.profiles ?? 0)}
        hint={stats && stats.profiles >= 1000 ? '1000+ (sampled)' : undefined}
      />
      <StatCard
        label="Banned Users"
        value={loading ? '…' : String(stats?.banned ?? 0)}
        accent="text-red-400"
      />
      <StatCard
        label="Disabled Games"
        value={String(disabledGames.size)}
        accent={disabledGames.size > 0 ? 'text-yellow-400' : undefined}
      />
      <StatCard
        label="Recent Game Results"
        value={loading ? '…' : String(stats?.recentGames ?? 0)}
        hint="last 100"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: string;
  accent?: string;
  hint?: string;
}) {
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <div className="text-xs text-gray-400 uppercase tracking-wide">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${accent ?? 'text-white'}`}>{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function GamesTab() {
  const { disabledGames, loading } = useAppConfig();
  const [pending, setPending] = useState<string | null>(null);

  const toggle = async (gameId: string, currentlyDisabled: boolean) => {
    setPending(gameId);
    try {
      await setGameDisabled(gameId, !currentlyDisabled);
      toast.success(
        !currentlyDisabled ? 'Game disabled — hidden from lobby' : 'Game re-enabled',
      );
    } catch (err) {
      toast.error('Failed to update game state');
      console.error(err);
    } finally {
      setPending(null);
    }
  };

  if (loading) {
    return (
      <div className="py-8 flex justify-center">
        <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-400 mb-4">
        Toggle games on or off. Disabled games stay registered but are hidden from the lobby.
      </p>
      {GAMES.map((game) => {
        const isDisabled = disabledGames.has(game.id);
        return (
          <div
            key={game.id}
            className="flex items-center justify-between bg-white/5 rounded-lg p-4 border border-white/10"
          >
            <div className="flex items-center gap-3">
              <div className="text-2xl">{game.icon}</div>
              <div>
                <div className="text-white font-semibold">{game.name}</div>
                <div className="text-xs text-gray-400">{game.description}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isDisabled ? (
                <span className="flex items-center gap-1 text-xs text-yellow-300">
                  <XCircle className="w-4 h-4" /> Disabled
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs text-green-300">
                  <CheckCircle2 className="w-4 h-4" /> Live
                </span>
              )}
              <button
                onClick={() => toggle(game.id, isDisabled)}
                disabled={pending === game.id}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${
                  isDisabled
                    ? 'bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30'
                    : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 border border-yellow-500/30'
                } disabled:opacity-50`}
              >
                {pending === game.id ? '…' : isDisabled ? 'Enable' : 'Disable'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UsersTab() {
  const adminUser = useAuthStore((s) => s.user);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'banned'>('all');
  const [busyUid, setBusyUid] = useState<string | null>(null);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const baseCol = collection(firestore, 'profiles');
      let q;
      if (activeQuery) {
        const term = activeQuery;
        q = query(
          baseCol,
          where('username', '>=', term),
          where('username', '<=', term + ''),
          limit(50),
        );
      } else if (filter === 'banned') {
        q = query(baseCol, where('is_banned', '==', true), limit(50));
      } else {
        q = query(baseCol, orderBy('created_at', 'desc'), limit(50));
      }
      const snap = await getDocs(q);
      const list: Profile[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Profile, 'id'>) }));
      setProfiles(list);
    } catch (err) {
      console.error('Failed to load profiles', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuery, filter]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveQuery(search.trim());
  };

  const banUser = async (target: Profile) => {
    if (target.id === adminUser?.uid) {
      toast.error("You can't ban yourself.");
      return;
    }
    const reason = window.prompt(`Reason for banning ${target.username}? (optional)`) ?? '';
    if (!window.confirm(`Ban ${target.username}? They will be blocked from playing.`)) return;
    setBusyUid(target.id);
    try {
      await updateDoc(doc(firestore, 'profiles', target.id), {
        is_banned: true,
        ban_reason: reason || '',
        banned_at: new Date().toISOString(),
      });
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === target.id
            ? { ...p, is_banned: true, ban_reason: reason, banned_at: new Date().toISOString() }
            : p,
        ),
      );
      toast.success(`Banned ${target.username}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to ban user');
    } finally {
      setBusyUid(null);
    }
  };

  const unbanUser = async (target: Profile) => {
    setBusyUid(target.id);
    try {
      await updateDoc(doc(firestore, 'profiles', target.id), {
        is_banned: false,
        ban_reason: '',
      });
      setProfiles((prev) =>
        prev.map((p) => (p.id === target.id ? { ...p, is_banned: false, ban_reason: '' } : p)),
      );
      toast.success(`Unbanned ${target.username}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to unban user');
    } finally {
      setBusyUid(null);
    }
  };

  const toggleAdmin = async (target: Profile) => {
    if (target.id === adminUser?.uid) {
      toast.error("You can't change your own admin flag.");
      return;
    }
    const willBeAdmin = !target.is_admin;
    if (
      !window.confirm(
        willBeAdmin
          ? `Grant admin to ${target.username}?`
          : `Revoke admin from ${target.username}?`,
      )
    ) {
      return;
    }
    setBusyUid(target.id);
    try {
      await updateDoc(doc(firestore, 'profiles', target.id), { is_admin: willBeAdmin });
      setProfiles((prev) =>
        prev.map((p) => (p.id === target.id ? { ...p, is_admin: willBeAdmin } : p)),
      );
      toast.success(willBeAdmin ? 'Admin granted' : 'Admin revoked');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update admin flag');
    } finally {
      setBusyUid(null);
    }
  };

  const lookupByUid = async () => {
    const uid = window.prompt('Enter user UID to look up:')?.trim();
    if (!uid) return;
    setLoading(true);
    try {
      const snap = await getDoc(doc(firestore, 'profiles', uid));
      if (!snap.exists()) {
        toast.error('No profile found for that UID');
        setProfiles([]);
        return;
      }
      setProfiles([{ id: snap.id, ...(snap.data() as Omit<Profile, 'id'>) }]);
      setActiveQuery('');
      setSearch('');
    } catch (err) {
      console.error(err);
      toast.error('Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <form onSubmit={submitSearch} className="flex-1 flex gap-2">
          <div className="flex-1 flex items-center bg-white/5 rounded-lg border border-white/10 px-3">
            <Search className="w-4 h-4 text-gray-400 shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username (prefix match)…"
              className="flex-1 bg-transparent px-2 py-2 text-white outline-none text-sm"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-lg text-sm font-semibold"
          >
            Search
          </button>
          {(activeQuery || search) && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setActiveQuery('');
              }}
              className="px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm"
            >
              Clear
            </button>
          )}
        </form>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as 'all' | 'banned')}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm"
          >
            <option value="all">Recent users</option>
            <option value="banned">Banned only</option>
          </select>
          <button
            onClick={lookupByUid}
            className="px-3 py-2 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-sm"
          >
            Lookup by UID
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 text-pink-500 animate-spin" />
        </div>
      ) : profiles.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">No users found.</div>
      ) : (
        <div className="space-y-2">
          {profiles.map((p) => (
            <UserRow
              key={p.id}
              profile={p}
              busy={busyUid === p.id}
              isSelf={p.id === adminUser?.uid}
              onBan={() => banUser(p)}
              onUnban={() => unbanUser(p)}
              onToggleAdmin={() => toggleAdmin(p)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UserRow({
  profile,
  busy,
  isSelf,
  onBan,
  onUnban,
  onToggleAdmin,
}: {
  profile: Profile;
  busy: boolean;
  isSelf: boolean;
  onBan: () => void;
  onUnban: () => void;
  onToggleAdmin: () => void;
}) {
  const createdLabel = useMemo(() => {
    if (!profile.created_at) return '';
    try {
      return new Date(profile.created_at).toLocaleDateString();
    } catch {
      return '';
    }
  }, [profile.created_at]);

  return (
    <div className="bg-white/5 rounded-lg p-4 border border-white/10 flex flex-col sm:flex-row gap-3 sm:items-center">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-white font-semibold truncate">{profile.username || '(no username)'}</span>
          {profile.is_admin && (
            <span className="text-xs bg-violet-500/20 text-violet-300 border border-violet-500/40 px-2 py-0.5 rounded">
              admin
            </span>
          )}
          {profile.is_banned && (
            <span className="text-xs bg-red-500/20 text-red-300 border border-red-500/40 px-2 py-0.5 rounded">
              banned
            </span>
          )}
          {isSelf && (
            <span className="text-xs bg-white/10 text-gray-300 px-2 py-0.5 rounded">you</span>
          )}
        </div>
        <div className="text-xs text-gray-400 truncate">
          {profile.email || '—'} · ELO {profile.elo_rating ?? 1000}
          {createdLabel ? ` · joined ${createdLabel}` : ''}
        </div>
        <div className="text-[10px] text-gray-500 font-mono truncate mt-0.5">{profile.id}</div>
        {profile.is_banned && profile.ban_reason && (
          <div className="text-xs text-red-300 mt-1">Reason: {profile.ban_reason}</div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          onClick={onToggleAdmin}
          disabled={busy || isSelf}
          className="px-3 py-1.5 text-xs rounded-md bg-violet-500/20 hover:bg-violet-500/30 text-violet-200 border border-violet-500/40 disabled:opacity-40"
        >
          {profile.is_admin ? 'Revoke admin' : 'Make admin'}
        </button>
        {profile.is_banned ? (
          <button
            onClick={onUnban}
            disabled={busy}
            className="px-3 py-1.5 text-xs rounded-md bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/40 disabled:opacity-50 flex items-center gap-1"
          >
            <RotateCcw className="w-3 h-3" /> Unban
          </button>
        ) : (
          <button
            onClick={onBan}
            disabled={busy || isSelf}
            className="px-3 py-1.5 text-xs rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-200 border border-red-500/40 disabled:opacity-40 flex items-center gap-1"
          >
            <Ban className="w-3 h-3" /> Ban
          </button>
        )}
      </div>
    </div>
  );
}
